// @spec SUP-STR-021
import { createFileRoute } from '@tanstack/react-router';
import { AlertCircle, Clock, FileX, XCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { PortalQuestionnaireForm, usePortalQuestionnaire } from '@/features/portal/questionnaire';

export const Route = createFileRoute('/portal/questionnaire/$token')({
  component: PortalQuestionnairePage,
});

/**
 * Loading skeleton for the questionnaire page.
 */
function QuestionnaireLoadingSkeleton() {
  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Header skeleton */}
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-32" />
          <div className="flex items-center justify-between">
            <div className="flex-1 space-y-2">
              <div className="flex items-center justify-between">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-4 w-8" />
              </div>
              <Skeleton className="h-2 w-full" />
            </div>
          </div>
        </div>

        {/* Section skeletons */}
        <div className="mt-8 space-y-4">
          {[1, 2].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-48" />
              </CardHeader>
              <CardContent className="space-y-6">
                {[1, 2, 3].map((j) => (
                  <div key={j} className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

interface ErrorStateProps {
  title: string;
  message: string;
  icon: React.ReactNode;
}

/**
 * Error state component.
 */
function ErrorState({ title, message, icon }: ErrorStateProps) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md text-center">
        <CardHeader>
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
            {icon}
          </div>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">{message}</p>
          <Button
            variant="outline"
            className="mt-6"
            onClick={() => {
              window.location.reload();
            }}
          >
            Try Again
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Portal questionnaire page.
 *
 * This is a PUBLIC page that allows suppliers to complete questionnaires
 * using a magic link token.
 */
function PortalQuestionnairePage() {
  const params = Route.useParams();
  const token = params.token;
  const { data, isLoading, isError, error } = usePortalQuestionnaire(token);

  // Loading state
  if (isLoading) {
    return <QuestionnaireLoadingSkeleton />;
  }

  // Error states
  if (isError || !data) {
    const typedError = error as (Error & { code?: string }) | null;
    const errorCode = typedError?.code;

    if (errorCode === 'TOKEN_EXPIRED') {
      return (
        <ErrorState
          title="Link Expired"
          message="This questionnaire link has expired. Please contact the organization for a new link."
          icon={<Clock className="h-8 w-8 text-muted-foreground" />}
        />
      );
    }

    if (errorCode === 'ALREADY_COMPLETED') {
      return (
        <ErrorState
          title="Already Submitted"
          message="This questionnaire has already been submitted. Thank you for your response."
          icon={<FileX className="h-8 w-8 text-muted-foreground" />}
        />
      );
    }

    if (errorCode === 'QUESTIONNAIRE_CLOSED') {
      return (
        <ErrorState
          title="Questionnaire Closed"
          message="This questionnaire is no longer accepting responses. Please contact the organization if you need assistance."
          icon={<XCircle className="h-8 w-8 text-muted-foreground" />}
        />
      );
    }

    return (
      <ErrorState
        title="Link Invalid"
        message="This questionnaire link is invalid. Please check the link and try again."
        icon={<AlertCircle className="h-8 w-8 text-muted-foreground" />}
      />
    );
  }

  // Render questionnaire form
  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
        <PortalQuestionnaireForm token={token} questionnaire={data} />
      </div>
    </div>
  );
}
